// ตัวช่วยร่วมของหน้าข้อสอบ — ป้ายชื่อและการแปลงรหัสคำตอบเพื่อแสดงผลเท่านั้น
// การตรวจและคิดคะแนนจริงอยู่ฝั่ง server ที่เดียว (server/utils/examScoring.js)
import Swal from 'sweetalert2'

export const API = process.env.REACT_APP_API

export const CHOICE_LABELS = ['ก', 'ข', 'ค', 'ง', 'จ']

// ชุดตัวอักษรบนช่องฝน (tb_exam.exam_labels) — เป็นแค่ป้าย รหัสที่เก็บ ('1'..'5' / 'T' 'F') เหมือนกันทุกชุด
export const LABEL_SETS = {
  th: { choice: CHOICE_LABELS, tf: ['ถ', 'ผ'], tfHelp: 'ถ = ใช่/ถูก, ผ = ไม่ใช่/ผิด' },
  en: { choice: ['A', 'B', 'C', 'D', 'E'], tf: ['T', 'F'], tfHelp: 'T = True, F = False' },
}
export const labelSet = (exam) => LABEL_SETS[exam?.exam_labels] || LABEL_SETS.th

export const TYPE_LABELS = {
  choice: 'ปรนัย',
  truefalse: 'ใช่/ไม่ใช่',
  written: 'อัตนัย',
}

export const isObjective = (q) => q.eq_type === 'choice' || q.eq_type === 'truefalse'

// รหัสคำตอบ ('1'..'5' / 'T' / 'F' / '#' / '') -> ตัวอักษรที่คนอ่าน ตามชุดของข้อสอบ (labelSet)
export const answerLabel = (code, labels = LABEL_SETS.th) => {
  if (!code) return ''
  if (code === 'T') return labels.tf[0]
  if (code === 'F') return labels.tf[1]
  if (code === '#') return '#'
  const i = Number(code) - 1
  return labels.choice[i] ?? code
}

// เฉลย '1,3' -> 'ก, ค' (หรือ 'A, C') / '*' -> 'ยกเลิกข้อ'
export const keyLabel = (key, labels = LABEL_SETS.th) => {
  if (!key) return ''
  if (key === '*') return '*'
  return key.split(',').map((c) => answerLabel(c, labels)).join(', ')
}

// เฉลยที่ครูพิมพ์/วาง -> ชุดรหัส ('1'..'5' / 'T' / 'F') สำหรับให้ปุ่มเลือกเฉลยรู้ว่าต้องติดปุ่มไหน
// รับรูปแบบเดียวกับ normalizeKey ฝั่ง server (1-5 / ก-จ / a-e / ถ ผ T F คั่นด้วยจุลภาค ช่องว่าง หรือ /)
// คืน { codes: Set, cancelled, invalid } — การตรวจจริงยังอยู่ที่ server ตัวนี้มีไว้แสดงผลเท่านั้น
export const parseKeyInput = (type, text, choices = 5) => {
  const raw = String(text ?? '').trim()
  if (raw === '*') return { codes: new Set(), cancelled: true, invalid: false }
  const codes = new Set()
  let invalid = false
  for (const part of raw.split(/[,\s/]+/).filter(Boolean)) {
    const ch = part.toUpperCase()
    let code = null
    if (type === 'truefalse') {
      if (['T', '1', 'ถ', 'Y'].includes(ch)) code = 'T'
      else if (['F', '2', 'ผ', 'N'].includes(ch)) code = 'F'
    } else {
      let idx = -1
      if (/^[1-5]$/.test(ch)) idx = Number(ch) - 1
      else if (/^[A-E]$/.test(ch)) idx = ch.charCodeAt(0) - 65
      else idx = CHOICE_LABELS.indexOf(ch)
      if (idx >= 0 && idx < choices) code = String(idx + 1)
    }
    if (code) codes.add(code)
    else invalid = true
  }
  return { codes, cancelled: false, invalid }
}

// คำตอบข้อปรนัยเรียงเป็นสตริงเดียว ("กข-ค") ไว้แสดง/แก้/เทียบ
//   readerText: ผลจากตัวอ่านกระดาษ { eq_no: '1' }
//   savedText:  คำตอบที่บันทึกแล้วจาก server { eq_no: { ea_answer: '1', ea_score } }
export const readerText = (answers, objective, labels) => objective.map((q) => answerLabel(answers?.[q.eq_no], labels) || '-').join('')
export const savedText = (answers, objective, labels) => objective.map((q) => answerLabel(answers?.[q.eq_no]?.ea_answer, labels) || '-').join('')

export const roomLabel = (r) => `ม.${r.c_id}/${r.r_no}`

export const fmt = (n, digits = 2) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '-'
  const v = Number(n)
  return Number.isInteger(v) ? String(v) : v.toFixed(digits).replace(/\.?0+$/, '')
}

export const showError = (err, title = 'ไม่สำเร็จ') => Swal.fire({
  icon: 'error',
  title,
  text: err?.response?.data?.message || err?.message || 'ลองใหม่อีกครั้ง',
})

// ปีการศึกษาที่เลือกไว้ ใช้คีย์เดียวกับหน้าอื่นของครูผู้สอน
// บางหน้าเคยเขียนค่า "undefined"/"null" ลงไป — ไม่ใช่ตัวเลขถือว่าไม่มี ไม่งั้นส่ง year_id=undefined ไปถาม server
export const readYearId = () => {
  try {
    const v = localStorage.getItem('yearId')
    return v && /^\d+$/.test(v) ? v : null
  } catch {
    return null
  }
}

export const FLAG_INFO = {
  negative: { label: 'ตรวจเฉลย', variant: 'danger', help: 'คนคะแนนสูงตอบข้อนี้ผิดมากกว่าคนคะแนนต่ำ ส่วนใหญ่แปลว่าเฉลยผิด' },
  distractor: { label: 'ตัวลวงแรง', variant: 'warning', help: 'กลุ่มคะแนนสูงเลือกตัวเลือกอื่นมากกว่าเฉลย' },
  lowdisc: { label: 'จำแนกต่ำ', variant: 'warning', help: 'คนเก่งกับคนอ่อนตอบถูกพอๆ กัน ข้อนี้แยกคนสองกลุ่มไม่ออก' },
  hard: { label: 'ยากมาก', variant: 'secondary', help: 'ตอบถูกไม่ถึง 20%' },
  easy: { label: 'ง่ายมาก', variant: 'info', help: 'ตอบถูกเกิน 80%' },
  cancelled: { label: 'ยกเลิกข้อ', variant: 'dark', help: 'ทุกคนได้คะแนนข้อนี้' },
  nokey: { label: 'ยังไม่มีเฉลย', variant: 'danger', help: 'ข้อนี้ยังไม่ถูกนับคะแนนให้ใคร' },
}

// กระดาษสำรอง: จับคู่เลขประจำตัวที่นักเรียนฝน (reader คืน idNumber) กับรายชื่อของข้อสอบ
// คืน { student, problem } — ไม่ตรงคนเดียวพอดี = ไม่เดา ให้ครูเลือกเอง
// แม้ตรงคนเดียวก็ยังต้องให้ครูยืนยันเสมอ: เลขที่ฝนไม่มี CRC แบบแถบรหัส ฝนผิดหลักเดียวตรงกับเพื่อนได้
export const matchSpare = (res, students) => {
  if (!res.idNumber) {
    return { student: null, problem: `กระดาษสำรอง — อ่านเลขประจำตัวที่ฝนไม่ได้ (${res.idRead || '?'}) เลือกนักเรียนเอง` }
  }
  const hits = students.filter((s) => String(s.st_idnumber || '').trim() === res.idNumber)
  if (hits.length === 1) return { student: hits[0], problem: null }
  return {
    student: null,
    problem: hits.length
      ? `กระดาษสำรอง — เลขประจำตัว ${res.idNumber} ตรงกับหลายคน เลือกนักเรียนเอง`
      : `กระดาษสำรอง — เลขประจำตัว ${res.idNumber} ไม่อยู่ในห้องที่ใช้ข้อสอบนี้ เลือกนักเรียนเอง`,
  }
}
